import urlConstants from '~/lib/constants/url.constants';

import api from './api';
import { CacheTag } from './CacheTag';
import { joinQueryParams } from '~/lib/util/api';
import { FetchTasksRequest, FetchTasksResponse } from '~/lib/util/task.util';
import { HttpRequestMethod } from '..';

type GetTaskSummaryQueryRequest = {
  inquiryId?: string;
  pageNumber?: number;
  pageSize?: number;
};

type UpdateTaskStatusRequest = {
  dischargePlanId?: string;
  inquiryId?: string;
  taskId: string;
  taskStatusCode: string;
  voidReasonExternalId?: string;
};

export const taskApi = api.injectEndpoints({
  endpoints: (build) => ({
    createComment: build.mutation<
      TaskCommentPostResponse,
      TaskCommentPostRequest
    >({
      invalidatesTags: [CacheTag.TaskComments],
      query: (req) => ({
        body: req,
        method: HttpRequestMethod.POST,
        responseHandler: 'text',
        url: urlConstants.tasks.comments,
      }),
    }),
    getTaskActivityByTaskId: build.query<
      TaskActivityEvent[],
      { taskExternalId: string }
    >({
      query: ({ taskExternalId }) => ({
        url: urlConstants.tasks.activityByTaskId(taskExternalId),
      }),
    }),
    getTaskCommentsByTaskId: build.query<
      TaskComment[],
      { taskExternalId: string }
    >({
      providesTags: [CacheTag.TaskComments],
      query: ({ taskExternalId }) => ({
        url: urlConstants.tasks.commentsByTaskId(taskExternalId),
      }),
    }),
    getTasks: build.query<FetchTasksResponse, FetchTasksRequest>({
      providesTags: [CacheTag.TasksFilterById],
      query: ({ filterBy }) => {
        const params = {};
        if (filterBy !== undefined) {
          params['filter-by'] = joinQueryParams(filterBy);
        }
        return {
          params,
          url: urlConstants.tasks.listTasks,
        };
      },
    }),

    getTaskSummaries: build.query<
      TaskSummaryResponse,
      GetTaskSummaryQueryRequest
    >({
      providesTags: (result, error, request) => [
        { id: request.inquiryId, type: CacheTag.InquiryTasksById },
      ],
      query: (params: GetTaskSummaryQueryRequest) => {
        return {
          params: {
            'inquiry-id': params.inquiryId,
            'page-number': params.pageNumber,
            'page-size': params.pageSize,
          },
          url: urlConstants.tasks.getSummaries,
        };
      },
    }),

    updateTaskStatus: build.mutation<void, UpdateTaskStatusRequest>({
      invalidatesTags: (result, error, request) => [
        { id: request.inquiryId, type: CacheTag.InquiryTasksById },
        { id: request.dischargePlanId, type: CacheTag.DischargePlanTasks },
      ],
      query: (body) => ({
        body: {
          taskStatusCode: body.taskStatusCode,
          voidReasonExternalId: body.voidReasonExternalId,
        },
        method: 'PUT',
        url: `${urlConstants.tasks.put}/${body.taskId}/status`,
      }),
    }),
  }),
});

export const {
  useCreateCommentMutation,
  useLazyGetTaskActivityByTaskIdQuery,
  useLazyGetTasksQuery,
  useLazyGetTaskCommentsByTaskIdQuery,
  useGetTaskSummariesQuery,
  useUpdateTaskStatusMutation,
} = taskApi;
